import React, { useEffect, useState } from "react";
import Content from "../../../../layout/content/Content";
import Head from "../../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockDes,
  BlockTitle,
  FormDatePicker,
  FormInput,
  Icon,
  LoadingComponent,
  PreviewCard,
  ReactDataTable,
  RSelect,
} from "../../../../components/Component";
import { PER_PAGE_ITEMS } from "../../../../utils";
import {
  listNmcNisccCheckAPI,
  nmcTableColumns,
  runScraperAPI,
  exportNmcNisccCheckAPI,
  fetchCheckDatesAPI,
} from "./NmcTableData";
import { scrollToTop, showSuccessToast, showErrorToast } from "../../../../utils/Functions";
import ImageViewModal from "../../../../components/ImageViewModal/ImageViewModal";
import NMCPDFViewModal from "../../../../components/PDFViewModal/NMCPDFViewModal";
import { useLocation } from "react-router-dom";
import { parseUrlParams, updateHash } from "../../../../utils/Utils";
import moment from "moment";
import { Modal, ModalBody, ModalHeader, ModalFooter, Button, Spinner } from "reactstrap";
import { ImportExportButton } from "../../../../components/button/ActionButton";

const NMC_STATUS_FILTER_OPTIONS = [
  { id: 0, label: "All status", value: "" },
  { id: 1, label: "Active", value: "Active" },
  { id: 2, label: "Expired", value: "Expired" },
  { id: 3, label: "Not Found", value: "Not Found" },
];

const NmcList = () => {
  const location = useLocation();
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState();
  const [totalItems, setTotalItems] = useState(0);
  const [initScreen, setInitScreen] = useState(true);
  const [sortId, setSortId] = useState();
  const [sortOrder, setSortOrder] = useState("asc");
  const [viewImageModal, setViewImageModal] = useState(false);
  const [viewPdfModal, setViewPdfModal] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [checkDate, setCheckDate] = useState("");
  const [latestCheckTimestamp, setLatestCheckTimestamp] = useState("");
  const [scraperModal, setScraperModal] = useState(false);
  const [scraperStatus, setScraperStatus] = useState("");
  const [scraperLoading, setScraperLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);

  // Fetch available check dates on mount
  useEffect(() => {
    fetchCheckDatesAPI({ pinType: "NMC" }, (response, error) => {
      if (error === null && response?.dates) {
        setAvailableDates(response.dates.map((d) => moment(d, "YYYY-MM-DD").toDate()));
      }
    });
  }, []);

  // Read URL params on mount and location change
  useEffect(() => {
    const params = parseUrlParams();

    if (params.page) {
      setCurrentPage(parseInt(params.page, 10));
    } else {
      setCurrentPage(1);
    }

    if (params.search) {
      const searchQuery = decodeURIComponent(params.search);
      setSearchText(searchQuery);
    }
    if (params.status) setStatusFilter(params.status || "");
    if (params.checkDate) setCheckDate(params.checkDate || "");
  }, [location]);

  useEffect(() => {
    if (!currentPage) return;
    loadData();
  }, [currentPage]);

  useEffect(() => {
    if (initScreen) return;
    if (currentPage === 1) {
      loadData();
    } else {
      setCurrentPage(1);
    }
  }, [searchText, sortId, sortOrder, statusFilter, checkDate]);

  const loadData = () => {
    scrollToTop();
    listNmcNisccCheckAPI(
      {
        pageInfo: {
          pageIndex: currentPage,
          itemsPerPage: PER_PAGE_ITEMS,
        },
        sorting: {
          field: sortId ?? null,
          order: sortOrder ?? null,
        },
        search: searchText ?? null,
        pinType: "NMC", // Filter for NMC only
        status: statusFilter || null,
        startDate: checkDate || null,
        endDate: checkDate || null,
      },
      (response, error) => {
        if (error === null) {
          setData(response.list || []);
          setTotalItems(response?.pageInfo?.totalItems ?? 0);
          setLatestCheckTimestamp(response?.latestCheckTimestamp ?? "");
        } else {
          console.error("Error loading NMC data:", error);
          setData([]);
        }
        setInitScreen(false);
      },
    );
  };

  const updateQueryParams = (updates) => {
    const hashParams = {
      page: currentPage,
      searchText: searchText,
      status: statusFilter,
      checkDate: checkDate,
      pathname: location.pathname,
      ...updates,
    };
    updateHash(hashParams);
  };

  const onPageChange = (page) => {
    setCurrentPage(page);
    updateQueryParams({ page: page });
  };

  const onSearchChange = (value) => {
    setSearchText(value);
    updateQueryParams({ searchText: value, page: 1 });
  };

  const onViewDocument = (row) => {
    // If PDF exists, show PDF viewer, otherwise show screenshot
    if (row?.pdfUrl) {
      setSelectedPdfUrl(row.pdfUrl);
      setViewPdfModal(true);
    } else if (row?.screenshotUrl) {
      setSelectedImageUrl(row.screenshotUrl);
      setViewImageModal(true);
    }
  };

  const onRunScraper = () => {
    setScraperLoading(true);
    runScraperAPI(
      {
        pinType: "NMC",
        status: scraperStatus || undefined,
      },
      (response, error) => {
        setScraperLoading(false);
        if (error === null) {
          showSuccessToast(response?.message || "NMC scraper started successfully");
          setScraperModal(false);
          setScraperStatus("");
        } else {
          showErrorToast(error?.message || "Failed to start NMC scraper");
        }
      },
    );
  };

  const onExport = () => {
    setExportLoading(true);
    exportNmcNisccCheckAPI(
      {
        search: searchText ?? null,
        pinType: "NMC",
        status: statusFilter || null,
        startDate: checkDate || null,
        endDate: checkDate || null,
        sorting: {
          field: sortId ?? null,
          order: sortOrder ?? null,
        },
      },
      (response, error) => {
        setExportLoading(false);
        if (error === null && response?.fileUrl) {
          window.open(response.fileUrl, "default");
        } else {
          showErrorToast(error?.message || "Failed to export NMC data");
        }
      },
    );
  };

  return (
    <React.Fragment>
      <Head title="NMC Check" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween className="flex-wrap">
            <BlockHeadContent>
              <BlockTitle page>NMC Check</BlockTitle>
              <BlockDes className="text-soft">
                <p className="mb-0">You have total {totalItems} records</p>
                {latestCheckTimestamp && (
                  <p className="mb-0 mt-1 small">
                    Latest NMC check: {moment(latestCheckTimestamp).format("DD-MM-YYYY HH:mm")}
                  </p>
                )}
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent className="ms-auto">
              <div className="d-flex align-items-center justify-content-center justify-content-lg-end flex-wrap">
                <span className="p-1">
                  <ImportExportButton
                    title="Export"
                    iconName="arrow-up"
                    onClick={onExport}
                    loading={exportLoading}
                  />
                </span>
                {/* <span className="p-1">
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => setScraperModal(true)}
                  >
                    <Icon name="play" />
                    <span>Run Scraper</span>
                  </Button>
                </span> */}
                <span className="p-1">
                  <RSelect
                    className="w-125px"
                    options={NMC_STATUS_FILTER_OPTIONS}
                    value={
                      NMC_STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter) ?? NMC_STATUS_FILTER_OPTIONS[0]
                    }
                    onChange={(selected) => {
                      const val = selected?.value ?? "";
                      setStatusFilter(val);
                      setCurrentPage(1);
                      updateQueryParams({ status: val, page: 1 });
                    }}
                  />
                </span>
                <span className="p-1 position-relative d-inline-block">
                  <div className="form-control-wrap">
                    <FormDatePicker
                      placeholderText="Select last check date"
                      selected={checkDate ? moment(checkDate, "YYYY-MM-DD").toDate() : null}
                      includeDates={availableDates.length > 0 ? availableDates : undefined}
                      onChange={(date) => {
                        const value = date ? moment(date).format("YYYY-MM-DD") : "";
                        setCheckDate(value);
                        setCurrentPage(1);
                        updateQueryParams({ checkDate: value, page: 1 });
                      }}
                    />
                  </div>
                  {checkDate && (
                    <span
                      className="position-absolute end-0 top-50 translate-middle-y pe-2 cursor-pointer text-soft hover-text-primary"
                      style={{ zIndex: 5 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCheckDate("");
                        setCurrentPage(1);
                        updateQueryParams({ checkDate: "", page: 1 });
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setCheckDate("");
                          setCurrentPage(1);
                          updateQueryParams({ checkDate: "", page: 1 });
                        }
                      }}
                      title="Clear date"
                    >
                      <Icon name="cross" />
                    </span>
                  )}
                </span>
                <span className="p-1">
                  <FormInput
                    labelGroupClass="m-0"
                    value={searchText ?? ""}
                    placeholder="Search"
                    onTextChange={onSearchChange}
                  />
                </span>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <Block>
          <PreviewCard>
            <ReactDataTable
              data={data}
              columns={nmcTableColumns(onViewDocument)}
              isAllowed={true}
              totalItemCount={totalItems}
              pagination
              pageNum={currentPage}
              onPageChanged={(page) => {
                onPageChange(page);
              }}
              onSort={(id) => {
                setSortId(id);
                const newSortOrder = sortOrder === "desc" ? "asc" : "desc";
                setSortOrder(newSortOrder);
              }}
            />
          </PreviewCard>
        </Block>
        <LoadingComponent isLoading={initScreen} />
        <ImageViewModal
          visible={viewImageModal}
          url={selectedImageUrl}
          onClosed={() => {
            setViewImageModal(false);
            setSelectedImageUrl(null);
          }}
          title="NMC Screenshot"
        />
        <NMCPDFViewModal
          visible={viewPdfModal}
          url={selectedPdfUrl}
          onClosed={() => {
            setViewPdfModal(false);
            setSelectedPdfUrl(null);
          }}
          title="NMC PDF"
        />
        <Modal isOpen={scraperModal} toggle={() => !scraperLoading && setScraperModal(false)}>
          <ModalHeader toggle={() => !scraperLoading && setScraperModal(false)}>Run NMC Scraper</ModalHeader>
          <ModalBody>
            <p>Select which status records to scrape:</p>
            <select
              className="form-control form-select"
              value={scraperStatus}
              onChange={(e) => setScraperStatus(e.target.value)}
              disabled={scraperLoading}
            >
              <option value="">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Expired">Expired</option>
              <option value="Not Found">Not Found</option>
            </select>
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              onClick={() => {
                setScraperModal(false);
                setScraperStatus("");
              }}
              disabled={scraperLoading}
            >
              Cancel
            </Button>
            <Button color="primary" onClick={onRunScraper} disabled={scraperLoading}>
              {scraperLoading ? <Spinner size="sm" className="me-1" /> : null}
              {scraperLoading ? "Starting..." : "Run"}
            </Button>
          </ModalFooter>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default NmcList;
